import { useRef, useState } from "react";
import { FileUp, FolderOpen, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { CATEGORIA_DAS_COMPRAS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { LinhaDaImportacao } from "./LinhaDaImportacao";
import { useImportacaoDeNotas } from "./use-importacao-de-notas";

const botao = "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted";

/**
 * Notas de fornecedor em lote, pra trazer o histórico: escolhe os XMLs (ou a pasta), confere a lista
 * já preenchida e importa — cada nota vira registro com o XML e, se quiser, a despesa já paga.
 */
export function ImportarNotasDialog({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const categorias = (trpc.financeiro.categorias.listar.useQuery(undefined, { enabled: aberto }).data ?? []).filter((c) => c.tipo === "SAIDA" && c.ativa);
  const padrao = categorias.find((c) => c.nome === CATEGORIA_DAS_COMPRAS)?.id ?? categorias[0]?.id ?? "";
  const imp = useImportacaoDeNotas(padrao);
  const arquivos = useRef<HTMLInputElement>(null);
  const pasta = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);

  const prontas = imp.linhas.filter((l) => l.situacao === "pronta");
  const incluidas = prontas.filter((l) => l.incluir && !l.resultado);
  const fora = imp.linhas.length - prontas.length;
  const concluido = imp.progresso !== null && !imp.gravando;
  const conta = (r: string) => imp.linhas.filter((l) => l.resultado?.resultado === r).length;
  const fechar = () => {
    if (imp.gravando) return;
    imp.limpar();
    onFechar();
  };

  const escolher = (lista: FileList | null) => imp.adicionar(Array.from(lista ?? []));

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-5xl flex-col gap-4 overflow-hidden border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>Importar notas de fornecedor</DialogTitle>
          <DialogDescription>
            Escolha os XMLs das notas (ou a pasta onde estão). Cada nota é registrada com o arquivo e pode virar despesa já paga. Recebeu um .zip? Descompacte antes.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => (e.preventDefault(), setArrastando(true))}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => (e.preventDefault(), setArrastando(false), escolher(e.dataTransfer.files))}
          className={cn(
            "flex flex-wrap items-center justify-center gap-3 rounded-lg border border-dashed px-4 text-sm text-zinc-600 dark:text-muted-foreground",
            imp.linhas.length ? "py-3" : "py-10",
            arrastando ? "border-brand-500 bg-brand-50/60 dark:bg-primary/5" : "border-zinc-300 dark:border-border",
          )}
        >
          {imp.lendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Arraste os XMLs aqui ou</span>}
          <button type="button" className={botao} disabled={imp.gravando} onClick={() => arquivos.current?.click()}>
            <FileUp className="h-4 w-4" /> Escolher arquivos
          </button>
          <button type="button" className={botao} disabled={imp.gravando} onClick={() => pasta.current?.click()}>
            <FolderOpen className="h-4 w-4" /> Escolher pasta
          </button>
          <input ref={arquivos} type="file" multiple accept=".xml" className="sr-only" onChange={(e) => (escolher(e.target.files), (e.target.value = ""))} />
          <input ref={pasta} type="file" multiple className="sr-only" {...({ webkitdirectory: "" } as object)} onChange={(e) => (escolher(e.target.files), (e.target.value = ""))} />
        </div>

        {imp.linhas.length > 0 && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-zinc-600 dark:text-muted-foreground">
              <span>
                {concluido
                  ? `${conta("importada")} importadas · ${conta("mesFechado")} só a nota (mês fechado) · ${conta("jaRegistrada")} já registradas · ${conta("erro") + conta("envio")} com erro`
                  : `${prontas.length} ${prontas.length === 1 ? "nota pronta" : "notas prontas"}${fora ? ` · ${fora} fora do lote` : ""}`}
              </span>
              {!concluido && categorias.length > 0 && (
                <label className="flex items-center gap-2 text-xs">
                  Categoria para todas
                  <select defaultValue="" onChange={(e) => e.target.value && imp.aplicarCategoriaATodas(e.target.value)} className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs dark:border-border dark:bg-card">
                    <option value="">Escolher</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <ul className="min-h-0 flex-1 divide-y divide-zinc-100 overflow-y-auto rounded-lg border border-zinc-200 dark:divide-border/50 dark:border-border">
              {imp.linhas.map((l) => (
                <LinhaDaImportacao key={l.id} linha={l} categorias={categorias} categoriaId={imp.categoriaDe(l)} bloqueada={imp.gravando} onMudar={(p) => imp.mudar(l.id, p)} />
              ))}
            </ul>
          </>
        )}

        <div className="flex items-center justify-end gap-2">
          {imp.progresso && imp.gravando && (
            <span className="mr-auto text-xs text-zinc-500">
              Importando {imp.progresso.feitas} de {imp.progresso.total}…
            </span>
          )}
          <button type="button" onClick={fechar} disabled={imp.gravando} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:text-muted-foreground dark:hover:bg-muted">
            {concluido ? "Concluir" : "Cancelar"}
          </button>
          {!concluido && (
            <button
              type="button"
              disabled={!incluidas.length || imp.gravando || imp.lendo}
              onClick={imp.gravar}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              {imp.gravando && <Loader2 className="h-4 w-4 animate-spin" />}
              Importar {incluidas.length} {incluidas.length === 1 ? "nota" : "notas"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
